import { useQuery, useMutation, useSubscription } from "./urqlHooks";

const GET_RECONCILE_RULES = `
  query GET_RECONCILE_RULES($filter: ReconcileRuleFilterInput) {
    reconcileRules(filter: $filter) {
      id
      companyId
      name
      description
      accountNumbers
      bankId
      providerRuts
      providerNames
      matchType
      action
      obligationFamily
      obligationFamilyLabel
      obligationType
      obligationTypeLabel
      obligationDescription
      accountPlan
      counterpartId
      counterpartName
      isReceived
      descriptionKeywords
      transactionType
      matchDocumentTypes
      obligationDescriptionKeywords
      obligationFolioKeywords
      obligationAmountMin
      obligationAmountMax
      obligationProviderRuts
      obligationProviderNames
      matchByAmount
      amount
      amountTolerance
      matchByDate
      day1
      day2
      day3
      day4
      day5
      isActive
      isSuggestion
      priority
      createdBy
      createdByName
      createdAt
      updatedAt
      sourceLabel
      suggestionMetadata
      isRejected
      isHidden
      rejectedAt
      rejectedBy
      rejectedByName
      isGlobal
      isGlobalActive
      globalRuleId
      exceptions {
        filters {
          kind
          value
          amount
          operator
          currency
          joinOperator
        }
      }
    }
  }
`;

const APPROVE_RECONCILE_RULE = `
  mutation APPROVE_RECONCILE_RULE($ruleId: String!, $companyId: String!) {
    approveReconcileRule(ruleId: $ruleId, companyId: $companyId)
  }
`;

export const ApproveReconcileRule = () => {
  const [result, executeMutation] = useMutation(APPROVE_RECONCILE_RULE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const REJECT_RECONCILE_RULE = `
  mutation REJECT_RECONCILE_RULE($ruleId: String!, $companyId: String!) {
    rejectReconcileRule(ruleId: $ruleId, companyId: $companyId)
  }
`;

export const RejectReconcileRule = () => {
  const [result, executeMutation] = useMutation(REJECT_RECONCILE_RULE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const ACTIVATE_GLOBAL_RULE = `
  mutation ACTIVATE_GLOBAL_RULE($globalRuleId: String!, $companyId: String!) {
    activateGlobalRule(globalRuleId: $globalRuleId, companyId: $companyId) {
      id
      name
      isGlobal
      isGlobalActive
      companyId
    }
  }
`;

export const ActivateGlobalRule = () => {
  const [result, executeMutation] = useMutation(ACTIVATE_GLOBAL_RULE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DEACTIVATE_GLOBAL_RULE = `
  mutation DEACTIVATE_GLOBAL_RULE($globalRuleId: String!, $companyId: String!) {
    deactivateGlobalRule(globalRuleId: $globalRuleId, companyId: $companyId)
  }
`;

export const DeactivateGlobalRule = () => {
  const [result, executeMutation] = useMutation(DEACTIVATE_GLOBAL_RULE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const ACTIVATE_GLOBAL_RULE_FOR_COMPANY = `
  mutation ACTIVATE_GLOBAL_RULE_FOR_COMPANY($globalRuleId: String!, $companyId: String!) {
    activateGlobalRuleForCompany(globalRuleId: $globalRuleId, companyId: $companyId) {
      id
      name
      isActive
      configs {
        companyId
        companyName
        isActive
        isHidden
      }
    }
  }
`;

export const ActivateGlobalRuleForCompany = () => {
  const [result, executeMutation] = useMutation(ACTIVATE_GLOBAL_RULE_FOR_COMPANY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DEACTIVATE_GLOBAL_RULE_FOR_COMPANY = `
  mutation DEACTIVATE_GLOBAL_RULE_FOR_COMPANY($globalRuleId: String!, $companyId: String!) {
    deactivateGlobalRuleForCompany(globalRuleId: $globalRuleId, companyId: $companyId) {
      id
      name
      isActive
      configs {
        companyId
        companyName
        isActive
        isHidden
      }
    }
  }
`;

export const DeactivateGlobalRuleForCompany = () => {
  const [result, executeMutation] = useMutation(DEACTIVATE_GLOBAL_RULE_FOR_COMPANY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DELETE_GLOBAL_RULE_FOR_COMPANY = `
  mutation DELETE_GLOBAL_RULE_FOR_COMPANY($globalRuleId: String!, $companyId: String!) {
    deleteGlobalRuleForCompany(globalRuleId: $globalRuleId, companyId: $companyId) {
      id
      name
      configs {
        companyId
        companyName
        isActive
        isHidden
      }
    }
  }
`;

export const DeleteGlobalRuleForCompany = () => {
  const [result, executeMutation] = useMutation(DELETE_GLOBAL_RULE_FOR_COMPANY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CREATE_GLOBAL_RULE_PERSONALIZATION = `
  mutation CREATE_GLOBAL_RULE_PERSONALIZATION($input: CreateReconcileRuleInput!) {
    createGlobalRulePersonalization(input: $input) {
      id
      name
      globalRuleId
      companyId
    }
  }
`;

export const CreateGlobalRulePersonalization = () => {
  const [result, executeMutation] = useMutation(CREATE_GLOBAL_RULE_PERSONALIZATION);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

export const GetReconcileRules = (filter) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_RECONCILE_RULES,
    variables: { filter },
    pause: !filter?.companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_COMPANY_RECONCILE_CONFIG = `
  query GET_COMPANY_RECONCILE_CONFIG($companyId: String!) {
    companyReconcileConfig(companyId: $companyId) {
      id
      company_id
      enable_strong_candidate_auto_match
      enable_exact_weak_candidates_auto_match
      enable_multiple_strong_candidates_oldest_auto_match
      enable_bank_commission_auto_match
    }
  }
`;

export const GetCompanyReconcileConfig = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_COMPANY_RECONCILE_CONFIG,
    variables: { companyId },
    pause: !companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const UPDATE_COMPANY_RECONCILE_CONFIG = `
  mutation UPDATE_COMPANY_RECONCILE_CONFIG($companyId: String!, $input: UpdateCompanyReconcileConfigInput!) {
    updateCompanyReconcileConfig(companyId: $companyId, input: $input) {
      id
      company_id
      enable_strong_candidate_auto_match
      enable_exact_weak_candidates_auto_match
      enable_multiple_strong_candidates_oldest_auto_match
      enable_bank_commission_auto_match
    }
  }
`;

export const UpdateCompanyReconcileConfig = () => {
  const [result, executeMutation] = useMutation(UPDATE_COMPANY_RECONCILE_CONFIG);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_GLOBAL_RECONCILE_RULES = `
  query GET_GLOBAL_RECONCILE_RULES($companyId: String!) {
    globalReconcileRules(companyId: $companyId) {
      id
      name
      description
      accountNumbers
      bankId
      providerRuts
      providerNames
      matchType
      action
      obligationFamily
      obligationFamilyLabel
      obligationType
      obligationTypeLabel
      obligationDescription
      accountPlan
      counterpartId
      counterpartName
      isReceived
      descriptionKeywords
      transactionType
      matchDocumentTypes
      obligationDescriptionKeywords
      obligationFolioKeywords
      obligationAmountMin
      obligationAmountMax
      obligationProviderRuts
      obligationProviderNames
      matchByAmount
      amount
      amountTolerance
      matchByDate
      day1
      day2
      day3
      day4
      day5
      isActive
      isSuggestion
      priority
      createdBy
      createdByName
      createdAt
      updatedAt
      sourceLabel
      suggestionMetadata
      isRejected
      isHidden
      rejectedAt
      rejectedBy
      rejectedByName
      configs {
        companyId
        companyName
        isActive
        isHidden
      }
      exceptions {
        filters {
          kind
          value
          amount
          operator
          currency
          joinOperator
        }
      }
    }
  }
`;

export const GetGlobalReconcileRules = (companyId) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_GLOBAL_RECONCILE_RULES,
    variables: { companyId },
    pause: !companyId,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const GET_RECONCILE_RULE = `
  query GET_RECONCILE_RULE($id: String!, $companyId: String!) {
    reconcileRule(id: $id, companyId: $companyId) {
      id
      companyId
      name
      description
      accountNumbers
      bankId
      providerRuts
      providerNames
      matchType
      action
      obligationFamily
      obligationFamilyLabel
      obligationType
      obligationTypeLabel
      obligationDescription
      accountPlan
      counterpartId
      counterpartName
      isReceived
      descriptionKeywords
      transactionType
      matchDocumentTypes
      obligationDescriptionKeywords
      obligationFolioKeywords
      obligationAmountMin
      obligationAmountMax
      obligationProviderRuts
      obligationProviderNames
      matchByAmount
      amount
      amountTolerance
      matchByDate
      day1
      day2
      day3
      day4
      day5
      isActive
      priority
      createdBy
      createdByName
      createdAt
      updatedAt
      exceptions {
        filters {
          kind
          value
          amount
          operator
          currency
          joinOperator
        }
      }
    }
  }
`;

export const GetReconcileRule = (id) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_RECONCILE_RULE,
    variables: { id },
    pause: !id,
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const CREATE_RECONCILE_RULE = `
  mutation CREATE_RECONCILE_RULE($input: CreateReconcileRuleInput!) {
    createReconcileRule(input: $input) {
      id
      name
      isActive
      isGlobal
      isGlobalActive
      companyId
    }
  }
`;

export const CreateReconcileRule = () => {
  const [result, executeMutation] = useMutation(CREATE_RECONCILE_RULE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const UPDATE_RECONCILE_RULE = `
  mutation UPDATE_RECONCILE_RULE($input: UpdateReconcileRuleInput!, $companyId: String!) {
    updateReconcileRule(input: $input, companyId: $companyId) {
      id
      name
      isActive
      isGlobal
      isGlobalActive
      companyId
      counterpartId
      counterpartName
    }
  }
`;

export const UpdateReconcileRule = () => {
  const [result, executeMutation] = useMutation(UPDATE_RECONCILE_RULE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const DELETE_RECONCILE_RULE = `
  mutation DELETE_RECONCILE_RULE($id: String!, $companyId: String!) {
    deleteReconcileRule(id: $id, companyId: $companyId)
  }
`;

export const DeleteReconcileRule = () => {
  const [result, executeMutation] = useMutation(DELETE_RECONCILE_RULE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const EXECUTE_FULL_RECONCILIATION = `
  mutation EXECUTE_FULL_RECONCILIATION($input: ExecuteFullReconciliationInput!) {
    executeFullReconciliation(input: $input) {
      success
      message
    }
  }
`;

// Actualizar el input type en el schema GraphQL (esto se hace automáticamente desde el backend)

export const ExecuteHybridReconciliation = () => {
  const [result, executeMutation] = useMutation(EXECUTE_FULL_RECONCILIATION);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

/** Pipeline completo Pascale (capas 0.1–0.9 + reglas), sin heurística/LLM. */
const EXECUTE_PASCALE_AUTOMATIONS = `
  mutation EXECUTE_PASCALE_AUTOMATIONS($input: ExecuteFullReconciliationInput!) {
    executePascaleAutomations(input: $input) {
      success
      message
      matched
      created
      errors
    }
  }
`;

export const ExecutePascaleAutomations = () => {
  const [result, executeMutation] = useMutation(EXECUTE_PASCALE_AUTOMATIONS);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

/** Solo capas (sin heurística/LLM); puede correr en paralelo al pipeline largo. */
const EXECUTE_LAYERS_ONLY_RECONCILIATION = `
  mutation EXECUTE_LAYERS_ONLY_RECONCILIATION($input: ExecuteFullReconciliationInput!) {
    executeLayersOnlyReconciliation(input: $input) {
      success
      message
    }
  }
`;

export const ExecuteLayersOnlyReconciliation = () => {
  const [result, executeMutation] = useMutation(EXECUTE_LAYERS_ONLY_RECONCILIATION);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const EXECUTE_AI_INVOICE_MOVEMENTS_ONLY = `
  mutation EXECUTE_AI_INVOICE_MOVEMENTS_ONLY($input: ExecuteStrongCandidatesOnlyInput!) {
    executeAiInvoiceMovementsOnly(input: $input) {
      success
      message
      matched
      total
      errors
    }
  }
`;

export const ExecuteAiInvoiceMovementsOnly = () => {
  const [result, executeMutation] = useMutation(EXECUTE_AI_INVOICE_MOVEMENTS_ONLY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const EXECUTE_STRONG_CANDIDATES_ONLY = `
  mutation EXECUTE_STRONG_CANDIDATES_ONLY($input: ExecuteStrongCandidatesOnlyInput!) {
    executeStrongCandidatesOnly(input: $input) {
      success
      message
      matched
      total
      errors
    }
  }
`;

export const ExecuteStrongCandidatesOnly = () => {
  const [result, executeMutation] = useMutation(EXECUTE_STRONG_CANDIDATES_ONLY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const EXECUTE_EXACT_CANDIDATES_ONLY = `
  mutation EXECUTE_EXACT_CANDIDATES_ONLY($input: ExecuteExactCandidatesOnlyInput!) {
    executeExactCandidatesOnly(input: $input) {
      success
      message
      matched
      total
      errors
    }
  }
`;

export const ExecuteExactCandidatesOnly = () => {
  const [result, executeMutation] = useMutation(EXECUTE_EXACT_CANDIDATES_ONLY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

// Fuerza la ejecución de la capa de combinaciones exactas (2-4 candidatos débiles).
const EXECUTE_WEAK_COMBINATIONS_ONLY = `
  mutation EXECUTE_WEAK_COMBINATIONS_ONLY($input: ExecuteExactCandidatesOnlyInput!) {
    executeWeakCombinationsOnly(input: $input) {
      success
      message
      matched
      total
      errors
    }
  }
`;

export const ExecuteWeakCombinationsOnly = () => {
  const [result, executeMutation] = useMutation(EXECUTE_WEAK_COMBINATIONS_ONLY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const EXECUTE_WEAK_CANDIDATES_ONLY = `
  mutation EXECUTE_WEAK_CANDIDATES_ONLY($input: ExecuteWeakCandidatesOnlyInput!) {
    executeWeakCandidatesOnly(input: $input) {
      success
      message
      matched
      total
      errors
    }
  }
`;

export const ExecuteWeakCandidatesOnly = () => {
  const [result, executeMutation] = useMutation(EXECUTE_WEAK_CANDIDATES_ONLY);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const EXECUTE_RECONCILE_RULE = `
  mutation EXECUTE_RECONCILE_RULE($ruleId: String!, $companyId: String!) {
    executeReconcileRule(ruleId: $ruleId, companyId: $companyId) {
      processed
      created
      matched
      skipped
      errors
    }
  }
`;

export const ExecuteReconcileRule = () => {
  const [result, executeMutation] = useMutation(EXECUTE_RECONCILE_RULE);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const EXECUTE_PYTHON_RECONCILIATION_AND_PROCESS = `
  mutation EXECUTE_PYTHON_RECONCILIATION_AND_PROCESS($input: ExecutePythonReconciliationInput!) {
    executePythonReconciliationAndProcess(input: $input) {
      success
      output
      error
      results
      stats {
        transactions {
          total
          matched
          unmatched
          byType
        }
        obligations {
          total
          paid
          unpaid
          byType
        }
      }
    }
  }
`;

export const ExecutePythonReconciliationAndProcess = () => {
  const [result, executeMutation] = useMutation(EXECUTE_PYTHON_RECONCILIATION_AND_PROCESS);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CONTINUE_RECONCILIATION = `
  mutation CONTINUE_RECONCILIATION($companyId: String!) {
    continueReconciliation(companyId: $companyId) {
      success
      output
      error
      results
      totalMatchesProcessed
      stats {
        transactions {
          total
          matched
          unmatched
          byType
        }
        obligations {
          total
          paid
          unpaid
          byType
        }
      }
    }
  }
`;

export const ContinueReconciliation = () => {
  const [result, executeMutation] = useMutation(CONTINUE_RECONCILIATION);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const RECONCILIATION_PROGRESS = `
  subscription RECONCILIATION_PROGRESS($companyId: String!) {
    reconciliationProgress(companyId: $companyId) {
      step
      message
      processed
      total
      created
      matched
      skipped
      errors
      percentage
      estimatedTime
      batchIndex
      totalBatches
      source
      layerKey
      result {
        success
        output
        error
        results
        totalMatchesProcessed
        stats {
          transactions {
            total
            matched
            unmatched
            byType
          }
          obligations {
            total
            paid
            unpaid
            byType
          }
        }
      }
    }
  }
`;

export const ReconciliationProgressSubscription = (companyId, onProgress) => {
  const handleSubscription = (messages = [], response) => {
    if (response && response.reconciliationProgress) {
      const progress = response.reconciliationProgress;
      if (onProgress) {
        onProgress(progress);
      }
      return [progress];
    }
    return messages;
  };

  const [res] = useSubscription(
    {
      query: RECONCILIATION_PROGRESS,
      variables: { companyId },
      pause: !companyId,
    },
    handleSubscription
  );

  return res;
};

const GET_RECONCILIATION_PROGRESS = `
  query GET_RECONCILIATION_PROGRESS($companyId: String!) {
    getReconciliationProgress(companyId: $companyId) {
      step
      message
      processed
      total
      created
      matched
      skipped
      errors
      percentage
      estimatedTime
      batchIndex
      totalBatches
      source
      layerKey
      result {
        success
        output
        error
        results
        totalMatchesProcessed
        stats {
          transactions {
            total
            matched
            unmatched
            byType
          }
          obligations {
            total
            paid
            unpaid
            byType
          }
        }
      }
    }
  }
`;

// Query para recuperar estado inicial (ej. tras recargar la página durante una reconciliación)
// Solo se usa para consultas manuales via reexecuteQuery, no para polling
export const GetReconciliationProgress = (companyId, shouldPoll = false) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_RECONCILIATION_PROGRESS,
    variables: { companyId },
    pause: !companyId || !shouldPoll, // Pausado por defecto; reexecuteQuery para consultas manuales
    requestPolicy: 'network-only', // Siempre consultar el servidor, no usar cache
  });
  const { data, fetching, error } = result;

  return { data, fetching, error, reexecuteQuery };
};

const UNDO_AUTOMATIC_MATCHES = `
  mutation UNDO_AUTOMATIC_MATCHES($input: UndoAutomaticMatchesInput!) {
    undoAutomaticMatches(input: $input) {
      success
      message
      matchesUndone
      errors
    }
  }
`;

export const UndoAutomaticMatches = () => {
  const [result, executeMutation] = useMutation(UNDO_AUTOMATIC_MATCHES);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const GET_RECONCILE_RULE_EXECUTIONS = `
  query GET_RECONCILE_RULE_EXECUTIONS(
    $ruleId: String
    $ruleName: String
    $companyId: String!
    $limit: Int
  ) {
    getReconcileRuleExecutions(
      ruleId: $ruleId
      ruleName: $ruleName
      companyId: $companyId
      limit: $limit
    ) {
      id
      executionId
      ruleId
      ruleName
      companyId
      executionDate
      executedBy
      executedByEmail
      executedByName
      action
      processed
      created
      matched
      skipped
      errorMessages
      status
      createdAt
    }
  }
`;

export const GetReconcileRuleExecutions = (variables) => {
  const [result, reexecuteQuery] = useQuery({
    query: GET_RECONCILE_RULE_EXECUTIONS,
    variables,
    // En backend normalmente se filtra por empresa; evitamos queries "huérfanas"
    pause: !variables?.companyId || (!variables?.ruleId && !variables?.ruleName),
  });
  const { data, fetching, error } = result;
  return { data, fetching, error, reexecuteQuery };
};

const UNDO_AUTOMATIC_MATCHES_BY_EXECUTION = `
  mutation UNDO_AUTOMATIC_MATCHES_BY_EXECUTION($executionId: String!, $companyId: String!) {
    undoAutomaticMatchesByExecution(executionId: $executionId, companyId: $companyId)
  }
`;

export const UndoAutomaticMatchesByExecution = () => {
  const [result, executeMutation] = useMutation(UNDO_AUTOMATIC_MATCHES_BY_EXECUTION);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CONVERT_LOCAL_RULE_TO_GLOBAL = `
  mutation CONVERT_LOCAL_RULE_TO_GLOBAL($localRuleId: String!, $companyId: String!) {
    convertLocalRuleToGlobal(localRuleId: $localRuleId, companyId: $companyId) {
      id
      name
      description
      action
      isActive
      priority
      accountNumbers
      bankId
      providerRuts
      providerNames
      matchType
      obligationFamily
      obligationFamilyLabel
      obligationType
      obligationTypeLabel
      transactionType
      matchByAmount
      matchByDate
      amount
      amountTolerance
      day1
      day2
      day3
      day4
      day5
      createdAt
      updatedAt
    }
  }
`;

export const ConvertLocalRuleToGlobal = () => {
  const [result, executeMutation] = useMutation(CONVERT_LOCAL_RULE_TO_GLOBAL);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

const CONVERT_GLOBAL_RULE_TO_LOCAL = `
  mutation CONVERT_GLOBAL_RULE_TO_LOCAL($globalRuleId: String!, $companyId: String!) {
    convertGlobalRuleToLocal(globalRuleId: $globalRuleId, companyId: $companyId) {
      id
      name
      description
      companyId
      action
      isActive
      priority
      accountNumbers
      bankId
      providerRuts
      providerNames
      matchType
      obligationFamily
      obligationFamilyLabel
      obligationType
      obligationTypeLabel
      transactionType
      matchByAmount
      matchByDate
      amount
      amountTolerance
      day1
      day2
      day3
      day4
      day5
      createdAt
      updatedAt
    }
  }
`;

export const ConvertGlobalRuleToLocal = () => {
  const [result, executeMutation] = useMutation(CONVERT_GLOBAL_RULE_TO_LOCAL);
  const { data, fetching, error } = result;
  return { data, fetching, error, executeMutation };
};

